import React, { Component } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import { confirmAlert } from 'react-confirm-alert';
import { toast } from 'react-toastify';
import SimpleReactValidator from 'simple-react-validator';

import services from '@C/CashiersOnDuty/_utils/data';
import { formatCurrency } from '@C/TableGrid';
import { authHeader } from '@H';

import CashDenominationDetails from './CashDenominationDetails';
import EditCashSummary from './EditCashSummary';

import './styles.scoped.scss';

const CASH_TYPES = [
  { cash_code: 1003, label: 'Cash in Hand' },
  { cash_code: 1000, label: 'Cash Received from Office' },
  { cash_code: 1001, label: 'Cash Given to Manager' },
  { cash_code: 1002, label: 'Cash Return to Office' },
];

class CashSummary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      cashier_id: this.props.match.params.staff,
      cashier_duty_id: this.props.match.params.duty,
      time_stamp: props.match.params.date,
      cashData: [],
      inputs: { 1000: '', 1001: '', 1002: '', 1003: '' },
      expanded: null,
      showModal: false,
      showDenominationModal: false,
      selectedEntry: null,
      selectedDenominationEntry: null,
      denominationDetails: null,
      isSubmitting: false,
      isLoadingDenomination: false,
    };
    this.validator = new SimpleReactValidator({
      autoForceUpdate: this,
      element: (message) => <div className="input-error">{message}</div>,
    });
  }

  isViewMode = () => {
    return (
      this.props.isViewMode ||
      (this.props.match?.params?.mode || '').includes('view-duty')
    );
  };

  componentDidMount() {
    this.getCashSummary();
  }

  getCashSummary = () => {
    services
      .getCashSummary(
        this.props.match.params.staff,
        this.props.match.params.date,
        this.state.cashier_duty_id,
      )
      .then((response) => {
        this.setState({ cashData: response.data.data || [] });
      })
      .catch((error) => {
        console.log(error);
      });
  };

  getEntriesForCode = (cash_code) => {
    return this.state.cashData.filter((item) => item.cash_code === cash_code);
  };

  getTotalForCode = (cash_code) => {
    return this.getEntriesForCode(cash_code).reduce(
      (sum, item) => sum + (item.amount || 0),
      0,
    );
  };

  handleInputChange = (cash_code, value) => {
    this.setState((prev) => ({
      inputs: { ...prev.inputs, [cash_code]: value },
    }));

    if (value) {
      this.validator.hideMessageFor(`amount_${cash_code}`);
    }
  };

  toggleExpand = (cash_code) => {
    this.setState((prev) => ({
      expanded: prev.expanded === cash_code ? null : cash_code,
    }));
  };

  submitEntry = (e, cash_code) => {
    if (this.isViewMode()) return;
    e.preventDefault();
    const amount = this.state.inputs[cash_code];
    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      this.validator.showMessageFor(`amount_${cash_code}`);
      this.forceUpdate();
      return;
    }
    this.setState({ isSubmitting: cash_code });
    const formData = {
      cashier_id: this.state.cashier_id,
      cashier_duty_id: this.state.cashier_duty_id,
      time_stamp: this.state.time_stamp,
      cash_list: [{ amount: Number(amount), cash_code }],
    };
    const headers = authHeader(1);
    axios
      .post(`${process.env.API_URL}/bunk/cash-management/`, formData, {
        headers,
      })
      .then(() => {
        toast.success('Cash entry added successfully');
        this.setState((prev) => ({
          inputs: { ...prev.inputs, [cash_code]: '' },
          isSubmitting: false,
        }));
        this.validator.hideMessageFor(`amount_${cash_code}`);
        this.forceUpdate();
        this.getCashSummary();
        this.props.onDataChange();
      })
      .catch((err) => {
        toast.error(err.response.data.message);
        this.setState({ isSubmitting: false });
      });
  };

  confirmDelete = (uuid) => {
    if (this.isViewMode()) return;
    confirmAlert({
      title: 'Confirm deletion',
      message: 'Are you sure you want to delete this entry?',
      buttons: [
        { label: 'Yes', onClick: () => this.deleteEntry(uuid) },
        { label: 'No', onClick: () => false },
      ],
    });
  };

  deleteEntry = (uuid) => {
    const headers = authHeader(1);
    axios
      .delete(
        `${process.env.API_URL}/bunk/cash-management/?cash_management_id=${uuid}&cashier_duty_id=${this.state.cashier_duty_id}`,
        { headers },
      )
      .then(() => {
        toast.success('Entry deleted successfully');
        this.getCashSummary();
        this.props.onDataChange();
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || 'Delete failed');
      });
  };

  openDenomination = ({ cash_code, label, amount }) => {
    this.setState({
      showModal: true,
      selectedEntry: {
        cash_code,
        cash_type: label,
        amount,
      },
    });
  };

  closeModal = () => {
    this.setState({ showModal: false, selectedEntry: null });
  };

  openDenominationDetails = (entry, label) => {
    if (!entry?.uuid) {
      toast.info('Denomination details is not available for that entry');
      return;
    }

    this.setState({ isLoadingDenomination: true });

    services
      .getCashDenominationDetails(
        this.state.cashier_duty_id,
        entry.cash_code,
        entry.uuid,
      )
      .then((response) => {
        const denominationDetails = response?.data?.data || {};
        const denominationRows = Array.isArray(
          denominationDetails.cash_denomination,
        )
          ? denominationDetails.cash_denomination
          : [];

        if (denominationRows.length < 1) {
          toast.info('Denomination details is not available for that entry');
          this.setState({ isLoadingDenomination: false });
          return;
        }

        this.setState({
          showDenominationModal: true,
          selectedDenominationEntry: {
            cash_code: entry.cash_code,
            cash_type: label,
            amount: entry.amount,
            uuid: entry.uuid,
          },
          denominationDetails,
          isLoadingDenomination: false,
        });
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.message ||
            'Failed to load denomination details',
        );
        this.setState({ isLoadingDenomination: false });
      });
  };

  closeDenominationModal = () => {
    this.setState({
      showDenominationModal: false,
      selectedDenominationEntry: null,
      denominationDetails: null,
    });
  };

  updateDenomination = () => {
    this.getCashSummary();
    this.props.onDataChange();
  };

  render() {
    const isViewMode = this.isViewMode();
    const { expanded, showModal, selectedEntry } = this.state;

    return (
      <>
        <section className="widget accordion-card">
          <header>
            <h3>Cash Summary</h3>
          </header>
          <main>
            <div className="accordion-container">
              {CASH_TYPES.map(({ cash_code, label }) => {
                const entries = this.getEntriesForCode(cash_code);
                const total = this.getTotalForCode(cash_code);
                const isExpanded = expanded === cash_code;
                const inputAmount = this.state.inputs[cash_code];

                return (
                  <div
                    key={cash_code}
                    className={`accordion-item${isExpanded ? ' active' : ''}`}
                  >
                    <header
                      className="accordion-header"
                      onClick={() => this.toggleExpand(cash_code)}
                    >
                      <h4>{label}</h4>
                      <span>{formatCurrency(total)}</span>
                    </header>
                    <div className="accordion-content">
                      {!isViewMode && (
                        <form
                          className="inline"
                          onSubmit={(e) => this.submitEntry(e, cash_code)}
                        >
                          <div className="input-wrapper with-label">
                            {this.validator.message(
                              `amount_${cash_code}`,
                              inputAmount,
                              'required|numeric|min:0,num',
                            )}
                            <label>Amount</label>
                            <input
                              name="amount"
                              type="text"
                              value={inputAmount}
                              onChange={(e) =>
                                this.handleInputChange(
                                  cash_code,
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                          <div
                            className="cta-wrapper"
                            style={{ gap: '0.5rem', alignItems: 'center' }}
                          >
                            <button
                              className={`submit${this.state.isSubmitting === cash_code ? ' loading' : ''}`}
                              type="submit"
                            >
                              Add
                            </button>

                            <button
                              className="actions __pay"
                              type="button"
                              onClick={() =>
                                this.openDenomination({
                                  cash_code,
                                  label,
                                  amount: total,
                                })
                              }
                            >
                              {isViewMode ? 'View' : 'Denomination'}
                            </button>
                          </div>
                        </form>
                      )}

                      <div className="table-wrapper">
                        <table>
                          <thead>
                            <tr>
                              <th>
                                <span>#</span>
                              </th>
                              <th>
                                <span>Amount</span>
                              </th>
                              <th>
                                <span>Time</span>
                              </th>
                              <th>
                                <span>Actions</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {entries.map((entry, idx) => (
                              <tr key={entry.uuid}>
                                <td>{idx + 1}</td>
                                <td>{formatCurrency(entry.amount)}</td>
                                <td>
                                  {entry.time_stamp
                                    ? new Date(
                                        entry.time_stamp,
                                      ).toLocaleString()
                                    : '-'}
                                </td>
                                <td
                                  className="item-actions"
                                  style={{
                                    alignItems: 'center',
                                    gap: '5px',
                                    display: 'flex',
                                  }}
                                >
                                  <button
                                    className="actions __view"
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      this.openDenominationDetails(
                                        entry,
                                        label,
                                      );
                                    }}
                                    disabled={this.state.isLoadingDenomination}
                                    aria-label={`View denomination details for ${label}`}
                                  ></button>
                                  {!isViewMode && (
                                    <button
                                      className="actions __delete"
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        this.confirmDelete(entry.uuid);
                                      }}
                                    >
                                      Delete
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </main>
        </section>
        <Modal
          isOpen={showModal}
          onRequestClose={this.closeModal}
          className="sub-xl-modal-container"
          overlayClassName="xl-modal-overlay"
          style={{
            content: {
              maxWidth: '50%',
            },
          }}
        >
          <div className="modal-component">
            {selectedEntry && (
              <EditCashSummary
                onRequestClose={this.closeModal}
                cash_code={selectedEntry.cash_code}
                cashier_id={this.state.cashier_id}
                cashier_duty_id={this.state.cashier_duty_id}
                time_stamp={this.state.time_stamp}
                cash_management_uuid={selectedEntry.uuid}
                cashManagementAmount={selectedEntry.amount || 0}
                cashManagementName={selectedEntry.cash_type || ''}
                updateDenomination={this.updateDenomination}
                readOnly={isViewMode}
              />
            )}
          </div>
        </Modal>
        <Modal
          isOpen={this.state.showDenominationModal}
          onRequestClose={this.closeDenominationModal}
          shouldCloseOnOverlayClick={true}
          shouldCloseOnEscape={true}
          className="sub-xl-modal-container"
          overlayClassName="xl-modal-overlay"
          style={{
            content: {
              maxWidth: '50%',
            },
          }}
        >
          <div className="modal-component">
            {this.state.selectedDenominationEntry &&
              this.state.denominationDetails && (
                <CashDenominationDetails
                  onRequestClose={this.closeDenominationModal}
                  cashDenominationList={this.state.denominationDetails}
                  cashManagementAmount={
                    this.state.selectedDenominationEntry.amount || 0
                  }
                  cashManagementName={
                    this.state.selectedDenominationEntry.cash_type || ''
                  }
                />
              )}
          </div>
        </Modal>
      </>
    );
  }
}

export default CashSummary;
